import React, { useState, useEffect } from 'react';
import { Button } from 'react-bootstrap';
import { FaCheckCircle } from 'react-icons/fa';
import './PathComponent.css';

/**
 * PathComponent - Displays a visual path/stage progression for picklist fields
 * Similar to Salesforce Path component
 *
 * @param {Object} field - The field configuration with is_path_field = true
 * @param {String} currentValue - The current value of the picklist field
 * @param {Function} onStageChange - Callback when user changes the stage
 * @param {Array} picklistValues - Array of picklist values/stages
 * @param {Boolean} isUpdating - Whether an update is in progress
 */
const PathComponent = ({ field, currentValue, onStageChange, picklistValues = [], isUpdating = false }) => {
  const [selectedStage, setSelectedStage] = useState(null);
  const [currentStageIndex, setCurrentStageIndex] = useState(-1);

  useEffect(() => {
    // Find the index of the current value
    const currentIndex = picklistValues.findIndex(
      pv => pv.key === currentValue || pv.value === currentValue
    );
    setCurrentStageIndex(currentIndex);
  }, [currentValue, picklistValues]);

  const handleStageClick = (stage, index) => {
    // Don't allow selection during update
    if (isUpdating) {
      return;
    }
    setSelectedStage(stage);
  };

  const handleMarkAsCurrent = () => {
    if (selectedStage && onStageChange && !isUpdating) {
      onStageChange(selectedStage.key || selectedStage.value);
      setSelectedStage(null);
    }
  };

  // Reset selected stage when update completes
  useEffect(() => {
    if (!isUpdating) {
      setSelectedStage(null);
    }
  }, [isUpdating]);

  if (!picklistValues || picklistValues.length === 0) {
    return null;
  }

  return (
    <div className={`path-component-container ${isUpdating ? 'updating' : ''}`}>
      <div className="path-component">
        <div className="path-stages">
          {picklistValues.map((stage, index) => {
            const isActive = index === currentStageIndex;
            const isSelected = selectedStage?.key === stage.key || selectedStage?.value === stage.value;
            const isCompleted = index < currentStageIndex;
            const isFuture = index > currentStageIndex;

            return (
              <div
                key={stage.key || stage.value || index}
                className={`path-stage ${isActive ? 'active' : ''} ${isSelected ? 'selected' : ''} ${isCompleted ? 'completed' : ''} ${isFuture ? 'future' : ''}`}
                onClick={() => handleStageClick(stage, index)}
                title={`${stage.label || stage.value}${isActive ? ' (Current)' : ''}${isCompleted ? ' (Completed)' : ''}${isSelected ? ' (Selected - Click button to update)' : ''}`}
                style={{
                  cursor: isUpdating ? 'not-allowed' : 'pointer',
                  opacity: isUpdating ? 0.6 : 1,
                  pointerEvents: isUpdating ? 'none' : 'auto'
                }}
              >
                <div className="stage-content">

                  <span className="stage-label mx-2">
                    {stage.label || stage.value}
                  </span>
                  {isCompleted && !isSelected && !isActive && (
                    <FaCheckCircle className="stage-icon completed-icon" style={{ marginRight: '6px', fontSize: '12px' }} />
                  )}
                </div>
                {index < picklistValues.length - 1 && (
                  <div className="stage-arrow"></div>
                )}
              </div>
            );
          })}
        </div>

        {selectedStage && !isUpdating && (
          <div className="path-actions">
            <Button
              variant="primary"
              size="sm"
              onClick={handleMarkAsCurrent}
              disabled={isUpdating}
              className="mark-stage-btn"
            >
              Mark as Current Stage
            </Button>
          </div>
        )}

        {isUpdating && (
          <div className="path-actions">
            <Button
              variant="primary"
              size="sm"
              disabled={true}
              className="mark-stage-btn"
            >
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Updating...
            </Button>
          </div>
        )}
      </div>

      {/*field?.field_label && (
        <div className="path-field-info">
          <small className="text-muted">
            Path: {field.field_label}
          </small>
        </div>
      )*/}
    </div>
  );
};

export default PathComponent;
